import { useFormik } from "formik";
import React, { useMemo, useState } from "react";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import BaseButton from "../../components/ui/BaseButton";
import BrandLogo from "../../assets/sidebar-nav/brand-logo.svg";
import BaseTextField from "../../components/ui/BaseTextField";
import { companySignup } from "../../services/api";
import { useDispatch } from "react-redux";
import { showToast } from "../../store/toast/toastSlice";
import BaseWrapper from "../../components/ui/BaseWrapper";
import BaseInnerWrapper from "../../components/ui/BaseInnerWrapper";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import PhoneInput from "react-phone-input-2";
// import "react-phone-input-2/lib/style.css";
import "react-phone-input-2/lib/material.css";
import PasswordChecker from "../../components/ui/PasswordChecker";
import useSignupTicket from "../../hooks/useSignupTicket";
import { setLoginSuccessful } from "../../store/authUser/authUserSlice";
import BaseCheckbox from "../../components/ui/BaseCheckbox";
const buildValidationSchema = (isSocial) =>
  yup.object({
    firstName: yup
      .string("Enter your first name")
      .matches(/^[A-Za-z\s]+$/, "First name must only contain letters")
      .required("First name is required"),
    lastName: yup
      .string("Enter your last name")
      .matches(/^[A-Za-z\s]+$/, "Last name must only contain letters")
      .required("Last name is required"),
    name: yup.string("Enter your company name").required("Company name is required"),
    phoneNumber: yup.string("Enter your phone number").required("Phone number is required"),
    email: yup
      .string("Enter your email")
      .email("Enter a valid email")
      .required("Email is required"),
    password: isSocial
      ? yup.string().notRequired()
      : yup
          .string()
          .required("Password is required")
          .min(8, "Password should be at least 8 characters long")
          .max(20, "Password should be at most 20 characters long")
          .test("has-uppercase", "Password must contain at least one uppercase letter", (value) =>
            /[A-Z]/.test(value),
          )
          .test("has-lowercase", "Password must contain at least one lowercase letter", (value) =>
            /[a-z]/.test(value),
          )
          .test("has-number", "Password must contain at least one number", (value) =>
            /[0-9]/.test(value),
          )
          .test("has-symbol", "Password must contain at least one symbol", (value) =>
            /[!@#$%^&*]/.test(value),
          ),
    companyAnnualRevenue: yup
      .string("Enter your annual revenue")
      .required("Company annual revenue is required"),
    /* noOfEmployees: yup
    .string("Enter your total employees ")
    .required("This field is required"), */
    /* technicianFrequency: yup
    .string("Enter this field")
    .required("This field is required"), */
    /* technicianUrgency: yup
    .string("Enter this field")
    .required("This field is required"), */
    referralSource: yup.string("Enter your source").required("Source is required"),
    isAgree: yup.boolean().oneOf([true], "You must agree to proceed").default(false),
  });

const CompanySignupForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isShowPassword, setIsShowPassword] = useState(false);
  const handleChangeChecked = (event) => {
    setChecked(event.target.checked);
  };
  const [checked, setChecked] = useState(false);

  // Present when the user got here by signing up with Google.
  const { ticket, prefill, isExpired, isSocial } = useSignupTicket();

  const {
    isPending: isSignupLoading,
    status,
    error,
    mutateAsync: signupMutation,
  } = useMutation({
    mutationFn: companySignup,
  });

  const formik = useFormik({
    // Prefill arrives a moment after mount, once the ticket has been read.
    enableReinitialize: true,
    initialValues: {
      firstName: prefill?.firstName ?? "",
      lastName: prefill?.lastName ?? "",
      name: "",
      phoneNumber: "",
      email: prefill?.email ?? "",
      password: "",
      companyAnnualRevenue: "",
      noOfEmployees: "",
      technicianFrequency: "",
      technicianUrgency: "",
      referralSource: "",
      isAgree: true,
    },
    validationSchema: useMemo(() => buildValidationSchema(isSocial), [isSocial]),
    onSubmit: (values) => {
      handleSignupButton();
    },
    validateOnChange: false,
    validateOnBlur: true,
  });

  const otpSentSuccessfully = () => {
    const email = encodeURIComponent(formik.values.email);

    navigate(`/otp?email=${email}`);
  };

  const handleSignupButton = async () => {
    try {
      const payload = {
        ...formik.values,
        phoneNumber: formik.values.phoneNumber.toString(),
        // The backend takes the email from the ticket rather than this payload, so a
        // tampered email field can't be used to register as somebody else.
        ...(isSocial ? { ticket, password: undefined } : {}),
      };
      const newUser = await signupMutation(payload);

      // Google already verified the address, so there is no OTP step — the backend
      // hands back tokens and we log them straight in.
      if (isSocial) {
        dispatch(
          setLoginSuccessful({
            user: newUser.data,
            accessToken: newUser.accessToken,
            refreshToken: newUser.refreshToken,
          }),
        );
        return;
      }

      otpSentSuccessfully();
    } catch (error) {
      if (error?.response?.status === 403) {
        const email = encodeURIComponent(formik.values.email);
        navigate(`/otp?email=${email}`);
      }
      dispatch(
        showToast({
          message: error?.response?.data?.message,
          type: "error",
        }),
      );
    }
  };

  return (
    <BaseWrapper>
      <BaseInnerWrapper className="h-full lg:py-16 py-8  2xl:mx-64 xl:mx-24 lg:mx-24 mx-0 xl:px-[70px] px-[0px]">
        <div className="lg:border border-zinc-300 rounded-lg  lg:py-10 lg:px-24  flex flex-col items-center">
          <div className=" flex flex-col justify-center items-center cursor-default">
            <img src={BrandLogo} width={110} />
            {/* Coming from Google, the account is already half made — "Let's create"
                would read as if the last step hadn't counted. */}
            <h1 className="text-4xl text-[#525354] text-center mt-6 mb-5 lg:leading-[70px] ">
              {isSocial ? "Complete your account setup" : "Let’s Create your account"}
            </h1>
            <p
              className={`text-base text-[#525354] text-center font-poppins px-8 text-[16px] ${
                isSocial ? "mb-10" : "mb-16"
              }`}
            >
              {isSocial ? (
                "Just a few details left and you’re done"
              ) : (
                <>
                  Looking to hire skilled technicians and post work orders? <br />
                  Just complete the form below to get started
                </>
              )}
            </p>

            {isExpired && (
              <div className="w-full border border-red-300 bg-red-50 rounded-lg px-8 py-6 mb-12">
                <p className="text-dark text-base text-center">
                  Your Google sign-in link has expired. Please{" "}
                  <Link className="text-primary underline underline-offset-4" to="/getStarted">
                    start again
                  </Link>
                  .
                </p>
              </div>
            )}
          </div>
          <div className="grid grid-cols-2 gap-x-3 gap-y-6  w-full">
            <div className="lg:col-span-1 col-span-2">
              <div className="lg:col-span-1 col-span-2">
                <BaseTextField
                  height="60px"
                  label={
                    <>
                      First Name <span className="text-red-500">*</span>
                    </>
                  }
                  labelShrink={true}
                  fullWidth
                  id="firstName"
                  name="firstName"
                  placeholder="William"
                  // Locked only when Google actually gave us a name — a disabled empty
                  // field would leave a required value with no way to fill it.
                  disabled={isSocial && Boolean(prefill?.firstName)}
                  value={formik.values.firstName}
                  onChange={(e) => {
                    // letters and spaces only, as on the tech signup form
                    if (/^[A-Za-z\s]*$/.test(e.target.value)) formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                  InputLabelProps={{
                    shrink: true,
                  }}
                />
              </div>
            </div>

            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                height="60px"
                label={
                  <>
                    Last Name <span className="text-red-500">*</span>
                  </>
                }
                labelShrink={true}
                fullWidth
                id="lastName"
                name="lastName"
                placeholder="Clark"
                disabled={isSocial && Boolean(prefill?.lastName)}
                value={formik.values.lastName}
                onChange={(e) => {
                  // letters and spaces only, as on the tech signup form
                  if (/^[A-Za-z\s]*$/.test(e.target.value)) formik.handleChange(e);
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                helperText={formik.touched.lastName && formik.errors.lastName}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
            {/* <div className="lg:col-span-1 col-span-2 ">
              <div>
                <PhoneInput
                  specialLabel=""
                  inputStyle={{
                    height: "100%",
                    width: "100%",
                    borderRadius: "8px",
                    ...(formik.touched.phoneNumber &&
                      formik.errors.phoneNumber && {
                        border: "1px solid #d32f2f",
                      }),
                  }}
                  className="h-full w-full rounded-full"
                  country={"au"}
                  id="phoneNumber"
                  name="phoneNumber"
                  placeholder=" 334761166"
                  label={
                    <>
                      Phone Number <span className="text-red-500">*</span>
                    </>
                  }
                  value={formik.values.phoneNumber}
                  onChange={(value) =>
                    formik.setFieldValue("phoneNumber", value)
                  } // Update formik field value
                  onBlur={() => formik.setFieldTouched("phoneNumber", true)} // Manually trigger touch
                  type="number"
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <FormHelperText error>
                    {formik.errors.phoneNumber}
                  </FormHelperText>
                )}
              </div>
            </div> */}
            <div className="lg:col-span-1 col-span-2 h-[60px]">
              <div>
                <PhoneInput
                  inputClass={`
        !w-full  !font-normal 
          !text-[#686868] !bg-white !border !rounded-lg 
        ${
          formik.touched.phoneNumber && formik.errors.phoneNumber
            ? "!border-red-600"
            : "!border-gray-300 focus:!border-[#0F40AE]"
        }
        focus:!outline-none focus:!ring-1 focus:!ring-[#0F40AE]
      `}
                  buttonClass="!border-none !bg-transparent"
                  specialLabel={
                    <span className="!text-[#686868] text-xs ">
                      Phone Number <span className="text-red-500">*</span>
                    </span>
                  }
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputStyle={{
                    width: "100%",
                    borderRadius: "8px",
                    fontSize: "13px",
                    padding: "14px 14px 11px 58px",
                    color: "#686868",
                    ...(formik.touched.phoneNumber &&
                      formik.errors.phoneNumber && {
                        border: "1px solid #d32f2f",
                      }),
                  }}
                  className="h-full w-full rounded-full"
                  country={"au"}
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formik.values.phoneNumber}
                  onChange={(value) => formik.setFieldValue("phoneNumber", value)} // Update formik field value
                  onBlur={() => formik.setFieldTouched("phoneNumber", true)} // Manually trigger touch
                  type="number"
                />
                {formik.touched.phoneNumber && formik.errors.phoneNumber && (
                  <FormHelperText error>{formik.errors.phoneNumber}</FormHelperText>
                )}
              </div>
            </div>

            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                height="60px"
                labelShrink={true}
                fullWidth
                id="email"
                name="email"
                label={
                  <>
                    Email <span className="text-red-500">*</span>
                  </>
                }
                placeholder="Email"
                disabled={isSocial}
                value={(isSocial && prefill?.emailMasked) || formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && Boolean(formik.errors.email)}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
            {/* No password when signing up with Google — the provider is the
                credential. They can still set one later via Forgot Password. */}
            {!isSocial && (
              <>
                <div className="col-span-2">
                  <BaseTextField
                    endAdornment={
                      <i
                        class={`fa-solid ${
                          isShowPassword ? "fa-eye" : "fa-eye-slash"
                        } text-base cursor-pointer`}
                        onClick={() => setIsShowPassword(!isShowPassword)}
                      ></i>
                    }
                    height="60px"
                    labelShrink={true}
                    fullWidth
                    id="password"
                    name="password"
                    placeholder="Hello@321"
                    label={
                      <>
                        Password <span className="text-red-500">*</span>
                      </>
                    }
                    type={isShowPassword ? "text" : "password"}
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.password && Boolean(formik.errors.password)}
                    helperText={formik.touched.password && formik.errors.password}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </div>
                <div className="col-span-2">
                  <PasswordChecker password={formik.values.password} />
                </div>
              </>
            )}
            <div className="lg:col-span-1 col-span-2">
              <BaseTextField
                height="60px"
                labelShrink={true}
                fullWidth
                id="name"
                name="name"
                label={
                  <>
                    Company Name <span className="text-red-500">*</span>
                  </>
                }
                placeholder="Tech Domain"
                value={formik.values.name}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.name && Boolean(formik.errors.name)}
                helperText={formik.touched.name && formik.errors.name}
                InputLabelProps={{
                  shrink: true,
                }}
              />
            </div>
            <div
              className={`lg:col-span-1 col-span-2 ${
                formik.touched.referralSource && formik.errors.referralSource
                  ? "h-[80px]"
                  : "h-[60px]"
              }`}
            >
              <FormControl
                fullWidth
                variant="outlined"
                style={{ height: "80%" }}
                error={
                  formik.touched.companyAnnualRevenue && Boolean(formik.errors.companyAnnualRevenue)
                }
              >
                <InputLabel id="companyAnnualRevenue-label" shrink>
                  Annual Revenue<span className="text-red-500">*</span>
                </InputLabel>
                <Select
                  labelId="companyAnnualRevenue-label"
                  id="companyAnnualRevenue"
                  name="companyAnnualRevenue"
                  value={formik.values.companyAnnualRevenue}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Annual revenue *"
                  className="rounded-lg"
                  displayEmpty
                  sx={{
                    fontSize: "0.875rem",
                    // "& .MuiSelect-select": { padding: "11px", color: "#686868" },
                  }}
                >
                  <MenuItem value="1" className="text-[#525354] text-sm">
                    Under $1 Million
                  </MenuItem>
                  <MenuItem value="5-10" className="text-[#525354] text-sm">
                    $5 - $10 Million
                  </MenuItem>
                  <MenuItem value="11" className="text-[#525354] text-sm">
                    Above $10 Million
                  </MenuItem>
                </Select>
                {formik.touched.companyAnnualRevenue && formik.errors.companyAnnualRevenue && (
                  <FormHelperText>{formik.errors.companyAnnualRevenue}</FormHelperText>
                )}
              </FormControl>
            </div>
            <div
              className={`col-span-2 ${
                formik.touched.referralSource && formik.errors.referralSource
                  ? "h-[80px]"
                  : "h-[60px]"
              }`}
            >
              <FormControl
                fullWidth
                required
                error={formik.touched.referralSource && Boolean(formik.errors.referralSource)}
                style={{ height: "100%" }} // Optional: Adjust the margin as needed
              >
                <InputLabel
                  id="referralSource"
                  required
                  shrink
                  sx={{
                    "&.MuiInputLabel-shrink": {
                      transform: "translate(14px, -9px) scale(0.75)",
                    },
                    "& .MuiFormLabel-asterisk": {
                      color: "red",
                      fontSize: "16px",
                    },
                  }}
                >
                  {" "}
                  Where did you hear about us?
                </InputLabel>
                <Select
                  className="rounded-lg"
                  labelId="referralSource"
                  id="referralSource"
                  name="referralSource"
                  placeholder="LinkedIn"
                  value={formik.values.referralSource}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  label="Where did you first hear about us?"
                  displayEmpty
                  sx={{
                    fontSize: "0.875rem",
                    "& .MuiSelect-select": { padding: "14px" },
                  }}
                >
                  <MenuItem value="LINKEDIN" className="text-[#525354] text-sm">
                    LinkedIn
                  </MenuItem>
                  <MenuItem value="INTERNET_SEARCH" className="text-[#525354] text-sm">
                    Internet Search
                  </MenuItem>
                  <MenuItem value="WORD_OF_MOUTH" className="text-[#525354] text-sm">
                    Word of mouth
                  </MenuItem>
                </Select>
                {formik.touched.referralSource && formik.errors.referralSource && (
                  <FormHelperText>{formik.errors.referralSource}</FormHelperText>
                )}
              </FormControl>
            </div>
            {/* <div className="col-span-2 mt-4">
              <p className="text-dark text-center font-normal text-base">
                You confirm you've read and agree to the{" "}
                <span className="text-primary">
                  User Agreement, Terms of Services
                </span>
                . 
              </p>
            </div>
          </div> */}
            <div className="flex items-center mt-3 col-span-2 justify-start ml-2">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checked}
                    onChange={handleChangeChecked}
                    color="primary"
                    style={{ padding: 0, margin: 0 }}
                  />
                }
                label=""
                style={{ marginRight: 0 }}
              />

              <p className="text-[#525354] font-medium text-sm mb-0" style={{ marginLeft: 10 }}>
                I confirm that I agree to the{" "}
                <span className="text-primary text-sm">Privacy Statement </span>
                <span className="text-base">& </span>
                <span className="text-primary text-sm">Terms of Services</span>
              </p>
            </div>
          </div>

          <BaseButton
            isLoading={isSignupLoading}
            onClick={formik.handleSubmit}
            className="mt-8 rounded-full px-[80px] py-[8px] text-base"
            color="primary"
            variant="contained"
            type="submit"
            disabled={!checked}
          >
            {isSocial ? "Complete Setup" : "Create Account"}
          </BaseButton>

          <p className="text-[#525354] text-base mt-8">
            Already have an account?{" "}
            <Link
              className="text-base underline underline-offset-[6px] text-primary font-normal"
              to="/login"
            >
              Login
            </Link>
          </p>
          <p className="text-[#525354] text-base font-medium mt-6">
            Copyright @2025 Tech Domain. All rights reserved.
          </p>
          {/* <p className="text-dark text-sm font-semibold my-4 text-center">
            <span className="underline underline-offset-[6px]">
              Privacy Statement
            </span>
            <span className="mx-2 font-light">|</span>
            <span className="underline underline-offset-[6px]">
              Legal agreements
            </span>
            <span className="mx-2 font-light">|</span>
            <span className="underline underline-offset-[6px]">Help</span>
            <span className="mx-2 font-light">|</span>
            <span className="underline underline-offset-[6px]">Contact</span>
          </p> */}
        </div>
      </BaseInnerWrapper>
    </BaseWrapper>
  );
};

export default CompanySignupForm;
